import React, { useRef, useState, useEffect } from "react";
import { Form, Overlay, Tooltip } from "react-bootstrap";

import authenticatedFetch from "../utils/fetch"

import DateTime from "react-datetime";

const NextActionDateCell = ({props, backgroundColor, count, currentDate, selectedDate}) => {
    const target = useRef(null);
    const [mouseOver, setMouseOver] = useState(false);
    const [showTooltip, setShowTooltip] = useState(false);

    var color = '#FEFFFF';
    var fontWeight = 'normal';
    if (currentDate.isSame(selectedDate, 'day'))
    {
        color = '#17252A';
        fontWeight = 'bold';
    }

    if (mouseOver)
    {
        color = '#2B7A78';
    }

    return (
        <>
        <td 
            ref={target}
            {...props} 
            className={`${props.className}`} 
            style={{backgroundColor: backgroundColor, color: color, fontWeight: fontWeight}} 
            onMouseOver={() => {setMouseOver(true)}} 
            onMouseOut={() => {setMouseOver(false)}}
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
            >
            {currentDate.date()}
        </td>

        <Overlay target={target.current} show={showTooltip} placement="top">
            {(overlayProps) => (
            <Tooltip {...overlayProps}>
                {`${count} task${count === 1 ? '' : 's'}`}
            </Tooltip>
            )}
        </Overlay>
        </>
    );
}

const UserPicker = ({taskId, disabled, localAssignees, selectedUserId, handleUserChanged}) => {
    const [error, setError] = useState(null);
    const [isLoaded, setIsLoaded] = useState(false);
    const [data, setData] = useState([]);

    // Task modals send these in as null to start
    if (!localAssignees)
    {
        localAssignees = [];
    }

    const selectedAssignees = localAssignees.map(assignee => assignee.value);

    useEffect(() => {
        authenticatedFetch({
          path: "task/duecountusers?selectedAssignees=" + selectedAssignees.join(","),
          additionalHeaders: {"taskId": taskId},
          successHandler: function(result) {
            setIsLoaded(true);
            setData(result);
            handleUserChanged(result[0].userId);
          },
          errorHandler: function(error) {
            setIsLoaded(true);
            setError(error);
          }
        });
    }, [localAssignees]);

    if (!isLoaded)
    {
        return (<Form.Select size="sm" disabled={true}><option value={0} key={0}>Loading...</option></Form.Select>);
    }
    else if (error || !data)
    {
        return (<Form.Select size="sm" disabled={true}><option value={0} key={0}>Error... Please reload the page...</option></Form.Select>);
    }
    else
    {
      let userOptionList = [];
      userOptionList.push(data.map((user, i) => {
        var label = "Show calendar for: "  + user.firstName + ' ' + user.lastName;
        if (user.isH3Admin)
        {
            label = label + " (H3)";
        }
        return (
          <option value={user.userId} key={user.userId}>{label}</option>
        )
      }, this));
  
      return (
        <Form.Select size="sm" disabled={disabled} onChange={(e) => {handleUserChanged(e.target.value, e)}} value={selectedUserId}>{userOptionList}</Form.Select>
      )
    }
};

const NextActionDatePicker = ({taskId, disabled, localDue, handleDueChanged, localAssignees, isInvalid=false}) => {
    const [error, setError] = useState(null);
    const [isLoaded, setIsLoaded] = useState(false);
    const [data, setData] = useState([]);

    const [userId, setUserId] = useState(0);

    // NEW: control open/close since we'll override day clicks
    const [isOpen, setIsOpen] = useState(false);
    const pickerRef = useRef(null);

    // Close calendar when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (pickerRef.current && !pickerRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const handleUserChanged = (userId, e) => {
        if (e != null) {
            e.preventDefault();
        }

        setUserId(userId);
    }

    const additionalHeaders = {};
    if (taskId)
    {
        additionalHeaders["taskId"] = taskId;
    }

    useEffect(() => {
        if (userId > 0)
        {
            authenticatedFetch({
                path: "task/duecounts/" + userId,
                additionalHeaders: additionalHeaders,
                successHandler: function(result) {
                    setIsLoaded(true);
                    setData(result);
                },
                errorHandler: function(error) {
                    setIsLoaded(true);
                    setError(error);
                }
            });
        }
    }, [userId])

    const dueMoment = DateTime.moment(localDue);

    const onDueChanged = (dueMoment) => {
        if (dueMoment && typeof dueMoment == 'object') 
        {
            handleDueChanged(dueMoment.format('YYYY-MM-DD'));
        }
        else
        {
            handleDueChanged('');
        }
    }

    const renderDay = (props, currentDate, selectedDate) => {
        // IMPORTANT: clone so we don't use react-datetime's mutable loop moment
        const day = currentDate.clone();
        const dateStr = day.format("YYYY-MM-DD");

        var opacity = '.7';
        if (props.className.includes('rdtOld') || props.className.includes('rdtNew')) 
        {
            opacity = '.5';
        }
      
        let backgroundColor = `rgb(58, 175, 169, ${opacity})`;
        if (data && data["counts"] && dateStr in data["counts"] && data["counts"][dateStr] > data['dangerThreshold'])
          backgroundColor = `rgb(217, 83, 79, ${opacity})`;
        else if (data && data["counts"] && dateStr in data["counts"] && data['counts'][dateStr] > data['warnThreshold'])
          backgroundColor = `rgb(229, 165, 75, ${opacity})`;
        else if (data && data["federalHolidays"] && data["federalHolidays"].includes(dateStr))
          backgroundColor = `rgb(70, 90, 95, ${opacity})`;
  
        const count = data?.counts && dateStr in data.counts ? data.counts[dateStr] : 0;

        // Override click to use the cloned day (and close the picker)
        const patchedProps = {
            ...props,
            onClick: (e) => {
                e?.preventDefault?.();
                e?.stopPropagation?.();
                onDueChanged(day);
                setIsOpen(false);
            }
        };

        return (
          <NextActionDateCell 
            key={dateStr} 
            props={patchedProps} 
            backgroundColor={backgroundColor} 
            count={count} 
            currentDate={currentDate} 
            selectedDate={selectedDate} />
        );
    };

    const renderView = (mode, renderDefault) => {
        if (mode === "time") return renderDefault();

        return (
          <div style={{padding: "15px", backgroundColor: "#FEFFFF"}}>
            <div style={{padding: "0 5px 10px 5px"}}>
                <UserPicker taskId={taskId} disabled={disabled} localAssignees={localAssignees} selectedUserId={userId} handleUserChanged={handleUserChanged} />
            </div>
            {renderDefault()}
          </div>
        );
    };

    return (
        <div ref={pickerRef}>
            <DateTime 
                inputProps={{
                    disabled: disabled, 
                    className: "form-control" + (isInvalid ? " is-invalid" : ""),
                    placeholder: "mm/dd/yyyy",
                    onFocus: () => setIsOpen(true),
                }}
                open={isOpen}
                onClose={() => setIsOpen(false)}
                value={dueMoment} 
                timeFormat={false} 
                closeOnSelect={false}   // we now close manually on day click
                renderDay={renderDay}
                renderView={renderView}
                onChange={(value) => {onDueChanged(value)}} />
        </div>
    )
}

export default NextActionDatePicker;